import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import muiTheme from './styles/muiTheme';
import { getMeActionInitiate } from './redux/Action/Auth/getMeAction';
import { useNotifications } from './hooks/useNotifications';

// ── Lazy-Loaded Pages (Route-based Code Splitting) ──
const OAuthCallback          = lazy(() => import('./Components/Login/OAuthCallback'));
const ForgotPassword        = lazy(() => import('./Components/Login/ForgotPassword'));
const ResetPassword         = lazy(() => import('./Components/Login/ResetPassword'));
const Landing                = lazy(() => import('./Components/Landing/Landing'));
const TermsOfService         = lazy(() => import('./Components/Landing/TermsOfService'));
const PrivacyPolicy          = lazy(() => import('./Components/Landing/PrivacyPolicy'));
const DataDeletion           = lazy(() => import('./Components/Landing/DataDeletion'));
const HowItWorks             = lazy(() => import('./Components/Landing/HowItWorks'));
const InvitePage             = lazy(() => import('./Components/Invite/InvitePage'));
const Login                  = lazy(() => import('./Components/Login/Login'));
const Signup                 = lazy(() => import('./Components/Login/Signup'));
const Otp                    = lazy(() => import('./Components/Login/Otp'));
const RoleSelection          = lazy(() => import('./Components/Login/RoleSelection'));
const CreatorOnboarding      = lazy(() => import('./Components/Creator/CreatorOnboarding'));
const BrandOnboarding        = lazy(() => import('./Components/Brand/BrandOnboarding'));
const AgencyOnboarding       = lazy(() => import('./Components/Agency/AgencyOnboarding'));

// ── Lazy-Loaded Dashboards ──
const CreatorDashboard       = lazy(() => import('./features/dashboard/creator/CreatorDashboard'));
const CreatorOverview        = lazy(() => import('./features/dashboard/creator/CreatorOverview'));
const BrandDashboard         = lazy(() => import('./features/dashboard/brand/BrandDashboard'));
const AgencyDashboard        = lazy(() => import('./features/dashboard/agency/AgencyDashboard'));
const AgencyOverview         = lazy(() => import('./features/dashboard/agency/AgencyOverview'));
const AgencyMessages         = lazy(() => import('./features/dashboard/agency/AgencyMessages'));
const AgencyWallet           = lazy(() => import('./features/dashboard/agency/AgencyWallet'));
const AgencyCreators         = lazy(() => import('./features/dashboard/agency/AgencyCreators'));
const AgencyAnalytics        = lazy(() => import('./features/dashboard/agency/AgencyAnalytics'));
const AgencyProfileSelf      = lazy(() => import('./features/dashboard/agency/AgencyProfilePage'));
const CreatorMessages        = lazy(() => import('./features/dashboard/creator/CreatorMessages'));
const CreatorWallet          = lazy(() => import('./features/dashboard/creator/CreatorWallet'));
const CreatorProfileSelf     = lazy(() => import('./features/dashboard/creator/CreatorProfilePage'));
const MyDeliverables         = lazy(() => import('./features/dashboard/creator/MyDeliverables'));
const CreatorAnalytics       = lazy(() => import('./features/dashboard/creator/CreatorAnalytics'));
const CreatorCalendarPage    = lazy(() => import('./features/dashboard/creator/CreatorCalendarPage'));
const BrandPublicProfilePage = lazy(() => import('./features/dashboard/creator/BrandPublicProfilePage'));
const CreatorProfilePage     = lazy(() => import('./features/dashboard/brand/CreatorProfilePage'));
const AgencyProfilePage      = lazy(() => import('./features/dashboard/brand/AgencyProfilePage'));
const NewCampaignPage        = lazy(() => import('./features/dashboard/brand/NewCampaignPage'));
const CampaignsPage          = lazy(() => import('./features/dashboard/brand/CampaignsPage'));
const ProposalsPage          = lazy(() => import('./features/dashboard/brand/ProposalsPage'));
const BrandTrackingView      = lazy(() => import('./features/dashboard/brand/BrandTrackingView'));
const BrandProfilePage       = lazy(() => import('./features/dashboard/brand/BrandProfilePage'));
const BrandMessages          = lazy(() => import('./features/dashboard/brand/BrandMessages'));
const BrandWallet            = lazy(() => import('./features/dashboard/brand/BrandWallet'));
const BrandAnalytics         = lazy(() => import('./features/dashboard/brand/BrandAnalytics'));
const AffiliateDashboard     = lazy(() => import('./features/dashboard/brand/AffiliateDashboard'));
const UgcLibraryPage         = lazy(() => import('./features/dashboard/brand/UgcLibraryPage'));
const AffiliateLinksView     = lazy(() => import('./features/dashboard/creator/AffiliateLinksView'));
const SettingsPage           = lazy(() => import('./features/dashboard/settings/SettingsPage'));
const VerifyEmailPage        = lazy(() => import('./Components/Login/VerifyEmailPage'));

const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <CircularProgress sx={{ color: 'var(--primary)' }} />
  </div>
);

// Redirects any unknown URL to the user's dashboard, or login if not authenticated
const CatchAll = () => {
  const { user, loading, initialized } = useSelector((s) => s.getMe);
  const token = localStorage.getItem('token');

  if (localStorage.getItem('is_selecting_role') === 'true') return <Spinner />;
  if (!token) return <Navigate to="/login" replace />;
  if (!initialized || loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/select-role" replace />;
  if (!user.onboarding_completed) return <Navigate to={`/${user.role}/onboarding`} replace />;
  return <Navigate to={`/${user.role}/dashboard`} replace />;
};

// Redirects logged-in users away from public pages (/, /login, /signup)
const PublicRoute = ({ children }) => {
  const { user, loading, initialized } = useSelector((s) => s.getMe);
  const token = localStorage.getItem('token');
  const location = useLocation();

  if (localStorage.getItem('is_selecting_role') === 'true') return <Spinner />;
  if (!token) return children;
  if (!initialized || loading) return <Spinner />;

  if (user) {
    const searchParams = new URLSearchParams(location.search);
    const ref = searchParams.get('ref') || searchParams.get('token');
    if (ref && ref.length > 15) {
      return <Navigate to={`/invite?ref=${ref}`} replace />;
    }

    if (!user.role) return <Navigate to="/select-role" replace />;
    if (!user.onboarding_completed) return <Navigate to={`/${user.role}/onboarding`} replace />;
    return <Navigate to={`/${user.role}/dashboard`} replace />;
  }
  return children;
};

// Guards a route: requires auth + optional role match
const PrivateRoute = ({ children, allowedRole }) => {
  const { user, loading, initialized } = useSelector((s) => s.getMe);
  const token = localStorage.getItem('token');

  if (localStorage.getItem('is_selecting_role') === 'true') return <Spinner />;
  if (!token) return <Navigate to="/login" replace />;
  if (!initialized || loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_completed && !window.location.pathname.includes('/onboarding')) {
    return <Navigate to={`/${user.role}/onboarding`} replace />;
  }
  if (allowedRole && user.role !== allowedRole) {
    return <Navigate to={`/${user.role}/dashboard`} replace />;
  }
  return children;
};

// Runs once on app load — checks token and redirects accordingly.
// Also mounts the global real-time notification WebSocket.
const AuthInit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Real-time notifications — active on every page for any logged-in user
  useNotifications();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const currentPath = window.location.pathname;

    // OAuth popup — skip all auth logic so we never clear localStorage in a popup window
    if (currentPath === '/oauth/callback') return;

    const isPublic = ['/', '/login', '/signup', '/select-role', '/verify-email'].includes(currentPath)
      || currentPath.startsWith('/signup/');

    dispatch(getMeActionInitiate(
      (user) => {
        if (!isPublic) return;
        const searchParams = new URLSearchParams(window.location.search);
        const ref = searchParams.get('ref') || searchParams.get('token');
        if (ref && ref.length > 15) {
          return navigate(`/invite?ref=${ref}`);
        }
        if (!user.role)                 return navigate('/select-role');
        if (!user.onboarding_completed) return navigate(`/${user.role}/onboarding`);
        navigate(`/${user.role}/dashboard`);
      },
      () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (!isPublic) navigate('/login');
      }
    ));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
      <Router>
        <AuthInit />
        <Suspense fallback={<Spinner />}>
          <Routes>
            {/* OAuth callback — opened in popup by OAuthCallback.jsx */}
            <Route path="/oauth/callback"      element={<OAuthCallback />} />

            {/* Public — redirect to dashboard if already logged in */}
            <Route path="/"               element={<PublicRoute><Landing /></PublicRoute>} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy"        element={<PrivacyPolicy />} />
            <Route path="/terms"          element={<TermsOfService />} />
            <Route path="/data-deletion"  element={<DataDeletion />} />
            <Route path="/how-it-works"   element={<HowItWorks />} />
            <Route path="/invite"         element={<InvitePage />} />
            <Route path="/login"          element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/signup"         element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/agency/signup"  element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/signup/otp"     element={<PublicRoute><Otp /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email"   element={<VerifyEmailPage />} />
            <Route path="/select-role"    element={<RoleSelection />} />

            {/* Onboarding — require auth but not onboarding_completed */}
            <Route path="/creator/onboarding"  element={<PrivateRoute allowedRole="creator"><CreatorOnboarding /></PrivateRoute>} />
            <Route path="/brand/onboarding"    element={<PrivateRoute allowedRole="brand"><BrandOnboarding /></PrivateRoute>} />
            <Route path="/agency/onboarding"   element={<PrivateRoute allowedRole="agency"><AgencyOnboarding /></PrivateRoute>} />

            {/* Creator */}
            <Route path="/creator/dashboard"  element={<PrivateRoute allowedRole="creator"><CreatorDashboard /></PrivateRoute>} />
            <Route path="/creator/overview"   element={<PrivateRoute allowedRole="creator"><CreatorOverview /></PrivateRoute>} />
            <Route path="/creator/messages"   element={<PrivateRoute allowedRole="creator"><CreatorMessages /></PrivateRoute>} />
            <Route path="/creator/affiliate"  element={<PrivateRoute allowedRole="creator"><AffiliateLinksView /></PrivateRoute>} />
            <Route path="/creator/calendar"   element={<PrivateRoute allowedRole="creator"><CreatorCalendarPage /></PrivateRoute>} />
            <Route path="/creator/analytics"  element={<PrivateRoute allowedRole="creator"><CreatorAnalytics /></PrivateRoute>} />
            <Route path="/creator/wallet"     element={<PrivateRoute allowedRole="creator"><CreatorWallet /></PrivateRoute>} />
            <Route path="/creator/profile"    element={<PrivateRoute allowedRole="creator"><CreatorProfileSelf /></PrivateRoute>} />
            <Route path="/creator/deliverables" element={<PrivateRoute allowedRole="creator"><MyDeliverables /></PrivateRoute>} />
            <Route path="/creator/brand/:id"  element={<PrivateRoute allowedRole="creator"><BrandPublicProfilePage /></PrivateRoute>} />

            {/* Brand */}
            <Route path="/brand/dashboard"               element={<PrivateRoute allowedRole="brand"><BrandDashboard /></PrivateRoute>} />
            <Route path="/brand/creator/:id"             element={<PrivateRoute allowedRole="brand"><CreatorProfilePage /></PrivateRoute>} />
            <Route path="/brand/agency/:id"              element={<PrivateRoute allowedRole="brand"><AgencyProfilePage /></PrivateRoute>} />
            <Route path="/brand/campaigns"               element={<PrivateRoute allowedRole="brand"><CampaignsPage /></PrivateRoute>} />
            <Route path="/brand/campaigns/new"           element={<PrivateRoute allowedRole="brand"><NewCampaignPage /></PrivateRoute>} />
            <Route path="/brand/campaigns/:id/proposals" element={<PrivateRoute allowedRole="brand"><ProposalsPage /></PrivateRoute>} />
            <Route path="/brand/campaigns/:id/track"     element={<PrivateRoute allowedRole="brand"><BrandTrackingView /></PrivateRoute>} />
            <Route path="/brand/campaigns/:id/affiliate" element={<PrivateRoute allowedRole="brand"><AffiliateDashboard /></PrivateRoute>} />
            <Route path="/brand/affiliate"               element={<PrivateRoute allowedRole="brand"><AffiliateDashboard /></PrivateRoute>} />
            <Route path="/brand/ugc"                     element={<PrivateRoute allowedRole="brand"><UgcLibraryPage /></PrivateRoute>} />
            <Route path="/brand/content-library"         element={<PrivateRoute allowedRole="brand"><UgcLibraryPage /></PrivateRoute>} />
            <Route path="/brand/messages"                element={<PrivateRoute allowedRole="brand"><BrandMessages /></PrivateRoute>} />
            <Route path="/brand/wallet"                  element={<PrivateRoute allowedRole="brand"><BrandWallet /></PrivateRoute>} />
            <Route path="/brand/analytics"               element={<PrivateRoute allowedRole="brand"><BrandAnalytics /></PrivateRoute>} />
            <Route path="/brand/profile"                 element={<PrivateRoute allowedRole="brand"><BrandProfilePage /></PrivateRoute>} />

            {/* Agency */}
            <Route path="/agency/dashboard"  element={<PrivateRoute allowedRole="agency"><AgencyDashboard /></PrivateRoute>} />
            <Route path="/agency/overview"   element={<PrivateRoute allowedRole="agency"><AgencyOverview /></PrivateRoute>} />
            <Route path="/agency/messages"   element={<PrivateRoute allowedRole="agency"><AgencyMessages /></PrivateRoute>} />
            <Route path="/agency/payments"   element={<PrivateRoute allowedRole="agency"><AgencyWallet /></PrivateRoute>} />
            <Route path="/agency/creators"   element={<PrivateRoute allowedRole="agency"><AgencyCreators /></PrivateRoute>} />
            <Route path="/agency/analytics"  element={<PrivateRoute allowedRole="agency"><AgencyAnalytics /></PrivateRoute>} />
            <Route path="/agency/profile"    element={<PrivateRoute allowedRole="agency"><AgencyProfileSelf /></PrivateRoute>} />
            <Route path="/agency/brand/:id"  element={<PrivateRoute allowedRole="agency"><BrandPublicProfilePage /></PrivateRoute>} />

            {/* Settings — shared page, role-aware */}
            <Route path="/creator/settings" element={<PrivateRoute allowedRole="creator"><SettingsPage /></PrivateRoute>} />
            <Route path="/brand/settings"   element={<PrivateRoute allowedRole="brand"><SettingsPage /></PrivateRoute>} />
            <Route path="/agency/settings"  element={<PrivateRoute allowedRole="agency"><SettingsPage /></PrivateRoute>} />

            {/* Catch-all — redirect to dashboard or login */}
            <Route path="*" element={<CatchAll />} />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default App;
